import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  AuditoriaCompleteRequest,
  AuditoriaStats,
  ItemDTO,
} from '@central/types';
import { api } from '@/lib/api';

interface PendingResponse {
  items: ItemDTO[];
  total: number;
}

export function useAuditoriaPending(): UseQueryResult<PendingResponse> {
  return useQuery({
    queryKey: ['auditoria', 'pending'],
    queryFn: () => api<PendingResponse>('/api/auditoria/pending'),
  });
}

export function useAuditoriaStats(): UseQueryResult<AuditoriaStats> {
  return useQuery({
    queryKey: ['auditoria', 'stats'],
    queryFn: () => api<AuditoriaStats>('/api/auditoria/stats'),
    refetchInterval: 60_000,
  });
}

export function useAuditoriaComplete(): ReturnType<
  typeof useMutation<ItemDTO, Error, { id: number; payload: AuditoriaCompleteRequest }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      api<ItemDTO>(`/api/auditoria/${id}/complete`, { method: 'PATCH', body: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auditoria'] });
      qc.invalidateQueries({ queryKey: ['items'] });
    },
  });
}

export function useAuditoriaSkip(): ReturnType<
  typeof useMutation<ItemDTO, Error, number>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api<ItemDTO>(`/api/auditoria/${id}/skip`, { method: 'PATCH' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auditoria'] });
    },
  });
}

export async function downloadApendiceAExport(): Promise<void> {
  const res = await fetch('/api/auditoria/export', {
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('central-access-token') ?? ''}`,
    },
  });
  if (!res.ok) throw new Error(`Export falhou: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const filename = res.headers
    .get('content-disposition')
    ?.match(/filename="([^"]+)"/)?.[1]
    ?? `apendice-a-${new Date().toISOString().slice(0, 10)}.md`;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
